import { CommonHelperFunctions, IImageLibraryDto } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

const readBlobAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result as string));
    reader.addEventListener("error", () => reject(reader.error));
    reader.readAsDataURL(blob);
  });

// Result of storing one image: where it now lives, plus the bytes as a self-contained data URI.
export interface IUploadedResource {
  resourceUrl: string;
  dataUrl: string;
}

/** Only these need the token; a `data:` URI, a `blob:` URL or a bundled `/images/…` path paints as is. */
const LIBRARY_FILE_URL_PATTERN: RegExp = /\/resource\/[0-9a-f-]{36}\/file$/i;

export const isLibraryFileUrl = (url: string): boolean => LIBRARY_FILE_URL_PATTERN.test(url);

const libraryFileUrl = (resourceId: string): string => `${backendUrl}/resource/${resourceId}/file`;

/** `rewritten`: new bytes are waiting under the same URL. `deleted`: the file is gone, nothing is coming. */
export type ResourceObjectUrlRetireReason = "rewritten" | "deleted";

/** Which file URLs were retired, or `undefined` when every cached URL was dropped. */
export type ResourceObjectUrlResetListener = (
  retiredFileUrls: readonly string[] | undefined,
  reason: ResourceObjectUrlRetireReason
) => void;

/**
 * One download per file for as long as the page lives — the lifetime the browser's own memory
 * cache gave a plain `<img>` (measured against 4.5). Do not drop these when a panel closes.
 */
const objectUrlPromises: Map<string, Promise<string>> = new Map<string, Promise<string>>();
/** The last object URL that landed, read synchronously so a remount paints without a blank frame. */
const objectUrls: Map<string, string> = new Map<string, string>();
const resetListeners: Set<ResourceObjectUrlResetListener> = new Set<ResourceObjectUrlResetListener>();
/**
 * File name → file URL. The backend keys writes by file name (a re-upload keeps the row's id,
 * hence its URL), so this maps a write back to the one cached URL it changed.
 */
const fileUrlByName: Map<string, string> = new Map<string, string>();

const notifyResetListeners = (
  retiredFileUrls: readonly string[] | undefined,
  reason: ResourceObjectUrlRetireReason
): void => {
  resetListeners.forEach((listener: ResourceObjectUrlResetListener): void => {
    listener(retiredFileUrls, reason);
  });
};

const revokeObjectUrl = (fileUrl: string): void => {
  const objectUrl: string | undefined = objectUrls.get(fileUrl);
  if (objectUrl === undefined) return;
  URL.revokeObjectURL(objectUrl);
  objectUrls.delete(fileUrl);
};

export class ImageLibraryService {
  public static async GetAll(): Promise<AxiosResponse<IImageLibraryDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/resource`);
      res.data.forEach((x) => {
        x.content = `${backendUrl}/${x.content}`;
      });
      fileUrlByName.clear();
      res.data.forEach((x: IImageLibraryDto): void => {
        if (isLibraryFileUrl(x.content)) fileUrlByName.set(x.fileName, x.content);
      });
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /** The cached file URLs the library holds under these names — the ones a write to them changes. */
  private static FileUrlsOf(fileNames: readonly string[]): string[] {
    const urls: string[] = [];
    fileNames.forEach((fileName: string): void => {
      const url: string | undefined = fileUrlByName.get(fileName);
      if (url !== undefined) urls.push(url);
    });
    return urls;
  }

  /** A write answered with the rows it stored: their names map to URLs from now on, list or no list. */
  private static RememberFileUrls(rows: readonly IImageLibraryDto[] | undefined): void {
    if (!Array.isArray(rows)) return;
    rows.forEach((row: IImageLibraryDto): void => {
      if (row.id && row.fileName) fileUrlByName.set(row.fileName, libraryFileUrl(row.id));
    });
  }

  /**
   * `resource/{id}/file` needs the bearer token a plain `<img src>` cannot carry, so the bytes are
   * fetched through axios and handed back as an object URL. A failed download is not remembered,
   * so the next paint retries; one a write or a delete overtook rejects rather than store bytes
   * that are already stale.
   */
  public static GetResourceObjectUrl(fileUrl: string): Promise<string> {
    const pending: Promise<string> | undefined = objectUrlPromises.get(fileUrl);
    if (pending) return pending;

    const download: Promise<string> = Axios.get<Blob>(fileUrl, { responseType: "blob" }).then(
      (res: AxiosResponse<Blob>): string => {
        if (objectUrlPromises.get(fileUrl) !== download) {
          throw new Error(`Resource ${fileUrl} download was superseded`);
        }
        if (!res.data || res.data.size === 0) {
          throw new Error(`Resource ${fileUrl} returned no content`);
        }
        const objectUrl: string = URL.createObjectURL(res.data);
        revokeObjectUrl(fileUrl);
        objectUrls.set(fileUrl, objectUrl);
        return objectUrl;
      }
    );
    objectUrlPromises.set(fileUrl, download);
    download.catch((): void => {
      if (objectUrlPromises.get(fileUrl) === download) objectUrlPromises.delete(fileUrl);
    });
    return download;
  }

  /** What can be painted for `fileUrl` right now: the last object URL that landed, if any. */
  public static PeekResourceObjectUrl(fileUrl: string): string | undefined {
    return objectUrls.get(fileUrl);
  }

  /**
   * A write changed the bytes behind these URLs while the URLs themselves stayed the same. Only
   * the download is forgotten, not the object URL: the old image keeps painting until the
   * replacement lands, because blanking every image for the length of a re-download read as a
   * flicker. A write that touches no cached URL (a new file name) tells nobody.
   */
  public static ResetResourceObjectUrls(fileUrls: readonly string[]): void {
    if (fileUrls.length === 0) return;
    fileUrls.forEach((fileUrl: string): void => {
      objectUrlPromises.delete(fileUrl);
    });
    notifyResetListeners(fileUrls, "rewritten");
  }

  /** These files were deleted — or, with nothing, all of them. No replacement is coming, so revoke now. */
  public static DropResourceObjectUrls(fileUrls?: readonly string[]): void {
    const dropping: readonly string[] =
      fileUrls ?? Array.from(new Set([...objectUrlPromises.keys(), ...objectUrls.keys()]));
    if (fileUrls !== undefined && dropping.length === 0) return;
    dropping.forEach((fileUrl: string): void => {
      objectUrlPromises.delete(fileUrl);
      revokeObjectUrl(fileUrl);
    });
    if (fileUrls === undefined) fileUrlByName.clear();
    notifyResetListeners(fileUrls, "deleted");
  }

  /** Returns the unsubscribe. */
  public static SubscribeResourceObjectUrlReset(listener: ResourceObjectUrlResetListener): () => void {
    resetListeners.add(listener);
    return (): void => {
      resetListeners.delete(listener);
    };
  }

  public static async AddOrUpdateResource(image: IImageLibraryDto[]): Promise<AxiosResponse> {
    try {
      // Only a name the library already holds has a cached URL to retire; a new name changes nothing on screen.
      const changedFileUrls: string[] = ImageLibraryService.FileUrlsOf(
        image.map((x: IImageLibraryDto): string => x.fileName)
      );
      const res: AxiosResponse<IImageLibraryDto[]> = await Axios.post<
        IImageLibraryDto[],
        AxiosResponse<IImageLibraryDto[]>
      >(`${backendUrl}/resource`, image);
      ImageLibraryService.RememberFileUrls(res.data);
      ImageLibraryService.ResetResourceObjectUrls(changedFileUrls);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // Reads a stored resource straight into a "data:" URI. Used by the email builder to embed template
  // images in the Unlayer design, because the editor's iframe (public origin) is barred by Chrome's
  // Local Network Access policy from fetching a private/on-prem API host itself. The id is resolved
  // against the CURRENT backend origin rather than the URL saved in the design, which may name a
  // host this deployment no longer answers on.
  public static async GetResourceAsDataUrl(resourceId: string): Promise<string> {
    const res = await Axios.get<Blob>(`${backendUrl}/resource/runtime/byid?resourceId=${resourceId}`, {
      responseType: "blob"
    });
    if (!res.data || res.data.size === 0) {
      throw new Error(`Resource ${resourceId} returned no content`);
    }
    return await readBlobAsDataUrl(res.data);
  }

  // Returns the stored resource URL alongside the bytes that were just uploaded. Callers that
  // render inside a third-party iframe need the data URI rather than the URL: a public-origin frame
  // cannot fetch our API when it sits on a private/loopback address (KOA-18358), and neither can
  // Unlayer's own server-side image proxy.
  public static async UploadFile(file: File): Promise<IUploadedResource> {
    const dataUrl: string = await readBlobAsDataUrl(file);
    const payload = {} as IImageLibraryDto;
    payload.fileName = file.name;
    payload.content = dataUrl;
    const changedFileUrls: string[] = ImageLibraryService.FileUrlsOf([file.name]);
    const res = await Axios.post<IImageLibraryDto[], AxiosResponse<IImageLibraryDto[]>>(`${backendUrl}/resource`, [
      payload
    ]);
    const uploaded = res.data?.find((r) => r.fileName === file.name) ?? res.data?.[res.data.length - 1];
    if (!uploaded?.id) {
      throw new Error("Upload failed: server returned no resource id");
    }
    ImageLibraryService.RememberFileUrls([uploaded]);
    ImageLibraryService.ResetResourceObjectUrls(changedFileUrls);
    return { resourceUrl: `${backendUrl}/resource/runtime/byid?resourceId=${uploaded.id}`, dataUrl };
  }

  public static async Delete(resourceName: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.delete<null>(`${backendUrl}/resource/${resourceName}`);
      ImageLibraryService.DropResourceObjectUrls(ImageLibraryService.FileUrlsOf([resourceName]));
      fileUrlByName.delete(resourceName);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}
